import React, { useEffect, useRef } from 'react';
import { MdOutlineSearch } from 'react-icons/md';
import { SearchOfIndicators } from '../../interfaces/Rights';
import Loading from '../Loading/Loading';
import { MdOutlineCancel } from "react-icons/md";
import useClickOutside from '../../hooks/useClickOutside';

interface IPredictiveSearchBar {
    placeholder: string;
    searchValue: string;
    searchResults: SearchOfIndicators[];
    isLoading: boolean;
    isOpen: boolean;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onSearch?: (value: string) => void;
    generalErrorMessage: string;
    searchErrorMessage: string;
    hasError: boolean;
    setHasError: React.Dispatch<React.SetStateAction<boolean>>;
    selectSearch: ({ indicator, indicatorID, breadcrumbs }: {
        indicator: string;
        indicatorID: number;
        breadcrumbs: string
    }) => void;
    cleanValue: boolean;
    onCleanValue: () => void;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const PredictiveSearchBar = ({
    placeholder,
    searchValue,
    searchResults,
    isLoading,
    isOpen,
    onChange,
    onSearch,
    generalErrorMessage,
    searchErrorMessage,
    hasError,
    setHasError,
    selectSearch,
    cleanValue,
    onCleanValue,
    setIsOpen,
}: IPredictiveSearchBar) => {
    const searchRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            if (!isOpen || !searchRef.current) return;

            const rect = searchRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;

            const isInputVisible = rect.bottom > 0 && rect.top < windowHeight;

            if (!isInputVisible) {
                setIsOpen(false);
                const input = searchRef.current.querySelector('input');
                input?.blur();
            }
        };

        if (window.innerWidth > 904) {
            window.addEventListener("scroll", handleScroll, { passive: true });
        }

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, [isOpen, setIsOpen])

    useClickOutside(searchRef, () => setIsOpen(false));

    return (
        <div className='w-full relative' ref={searchRef}>
            <SearchInput
                placeholder={placeholder}
                searchValue={searchValue}
                onChange={onChange}
                onSearch={onSearch}
                setHasError={setHasError}
                cleanValue={cleanValue}
                onCleanValue={onCleanValue}
                setIsOpen={setIsOpen}
                searchResults={searchResults}
            />
            <HelperOrError hasError={hasError} errorMessage={generalErrorMessage} />
            {isOpen && <SearchResultsContainer
                isLoading={isLoading}
                searchResults={searchResults}
                searchErrorMessage={searchErrorMessage}
                selectSearch={selectSearch}
                isOpen={isOpen}
            />}
        </div>
    );
};

const SearchInput = ({
    placeholder,
    searchValue,
    onChange,
    onSearch,
    setHasError,
    cleanValue,
    onCleanValue,
    setIsOpen,
    searchResults,
}: Pick<IPredictiveSearchBar, 'placeholder' | 'searchValue' | 'onChange' | 'onSearch' | 'setHasError' | 'cleanValue' | 'onCleanValue' | 'setIsOpen' | 'searchResults'>) => {
    const handleFocus = () => {
        setHasError(false);
        if (searchResults?.length && searchValue.length >= 3) {
            setIsOpen(true);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && searchValue.trim().length >= 3 && onSearch) {
            onSearch?.(searchValue);
        }
    };

    return (
        <label htmlFor="search" className='flex items-center'>
            <MdOutlineSearch size={24} id='icon' className='absolute ml-2' color='#40484C' />
            <input
                className='border border-system-light-50 text-blarge text-system-light-100 placeholder:text-system-light-40 w-full h-full rounded-sm focus:outline-none focus:ring-2 focus:ring-primary-40 px-10 py-4 hover:border-system-light-100 search-predictive'
                id='search'
                name='search'
                placeholder={placeholder}
                value={searchValue}
                aria-label={placeholder}
                onChange={onChange}
                onFocus={handleFocus}
                onBlur={() => setHasError(false)}
                autoComplete='off'
                onKeyDown={handleKeyDown}
            />
            {
                cleanValue && <button className='absolute right-[10px] ' onClick={onCleanValue}><MdOutlineCancel size={24} color='#40484C' /></button>
            }
        </label>
    )
};

const HelperOrError = ({ hasError, errorMessage }: { hasError: boolean; errorMessage: string }) => (
    <span className={`block text-bsmall md:text-bmedium mt-1 ${hasError ? 'text-indicator-red' : 'text-system-light-40'}`}>
        {hasError ? errorMessage : 'Debe ingresar al menos 3 caracteres.'}
    </span>
);

const SearchResultsContainer = ({
    isLoading,
    searchResults,
    searchErrorMessage,
    selectSearch,
    isOpen
}: {
    isLoading: boolean;
    searchResults: SearchOfIndicators[];
    searchErrorMessage: string;
    selectSearch: ({ indicator, indicatorID, breadcrumbs }: {
        indicator: string;
        indicatorID: number;
        breadcrumbs: string
    }) => void;
    isOpen: boolean
}) => (
    <div
        className={`
            w-full absolute py-3 z-50 bg-primary-100 top-[58px] mt-1 max-h-[260px] rounded-sm shadow-custom-dual overflow-y-auto
            transition-all duration-200 ease-in-out transform origin-top
            ${isOpen ? "opacity-100 scale-100 visible" : "opacity-0 scale-95 invisible pointer-events-none"}
        `}
    >        {isLoading ? (
        <Loading size={30} color='#0E6681' />
    ) : (
        <>
            {searchResults && searchResults.length > 0 ? (
                searchResults.map(({ titulo, subdimension, id, breadcrumbs }, index) => (
                    <SearchResult
                        key={`${id}-${index}`}
                        titulo={titulo}
                        subdimension={subdimension}
                        selectSearch={selectSearch}
                        indicatorID={id}
                        breadcrumbs={breadcrumbs}
                    />
                ))
            ) : (
                <p className='text-center'>{searchErrorMessage}</p>
            )}
        </>
    )}
    </div>
);

const SearchResult = ({
    titulo,
    selectSearch,
    indicatorID,
    breadcrumbs,
}: {
    titulo: string;
    subdimension: string;
    indicatorID: number;
    selectSearch: ({ indicator, indicatorID, breadcrumbs }: {
        indicator: string;
        indicatorID: number;
        breadcrumbs: string
    }) => void;
    breadcrumbs: string;
}) => (
    <button
        onClick={() => selectSearch({ indicator: titulo, indicatorID: indicatorID, breadcrumbs })}
        className='py-3 px-4 w-full hover:bg-brand-green-5 transition-all duration-200 cursor-pointer text-left ease-in-out'
    >
        <p className='text-system-light-100 text-bmedium'>{titulo}</p>
        <p className='text-primary-40 text-bsmall'>{breadcrumbs}</p>
    </button>
);

export default PredictiveSearchBar;